import { useQuery } from '@tanstack/react-query'
import { BranchesService } from '@/shared/api/rest/services/branches.service'
import { TeachersService } from '@/shared/api/rest/services/teachers.service'
import { BranchRecord } from '@/shared/api/rest/types'
import { RegistryTeacher } from '@/pages/user/teacher/teachers/types'

// The backend's default limit is 20; the registry is small enough to fetch whole.
const LIST_LIMIT = 1000

/** Shared cache entry — the registry page and the notification bell both read it. */
export const TEACHER_REGISTRY_KEY = ['registry-teachers']

/**
 * Fetch the full teacher registry and attach a resolved branch.
 *
 * `GET /teachers` applies its `status` filter conditionally, so omitting the
 * param returns every status — one call covers pending, approved and suspended
 * teachers alike. Rows are bulk-enriched server-side with name/email/phone/
 * picture from `public.users`, but `branch` is only resolved on the single-record
 * endpoints, so the branch name is joined here from the branches list.
 */
export const useTeacherRegistry = (
    options: { enabled?: boolean; refetchInterval?: number } = {}
) => {
    const query = useQuery({
        queryKey: TEACHER_REGISTRY_KEY,
        enabled: options.enabled ?? true,
        // Stop polling once the request fails. `GET /teachers` is guarded on the
        // `admin.teacher` *permission*, while the client infers admin status from
        // the `admin_teacher` role too — a user with the role but not the
        // permission would otherwise 403 on a loop every interval.
        refetchInterval: (query) =>
            query.state.status === 'error' ? false : options.refetchInterval ?? false,
        queryFn: async () => {
            const [res, branches] = await Promise.all([
                TeachersService.list({ limit: LIST_LIMIT }),
                BranchesService.list().catch(() => [] as BranchRecord[])
            ])

            return (res.data ?? []).map<RegistryTeacher>((teacher) => ({
                ...teacher,
                branch:
                    teacher.branch ??
                    (teacher.branchId
                        ? {
                              _id: teacher.branchId,
                              name:
                                  branches.find((b) => b._id === teacher.branchId)
                                      ?.name ?? ''
                          }
                        : null)
            }))
        }
    })

    return {
        teachers: query.data ?? [],
        loading: query.isLoading,
        fetching: query.isFetching,
        error: query.error ?? null
    }
}
